import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CheckoutEnrollment } from '@spike/membership-model';
import { OrderItem } from '@spike/order-model';
import clsx from 'clsx';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import styles from './SelectorMembership.module.scss';
import SelectorMembershipItem from './SelectorMembershipItem';

interface SelectorMembershipProps {
    orderItem: OrderItem;
    placeholder?: string;
    enrollments: Array<CheckoutEnrollment>;
    selectedEnrollmentId?: number;
    disabled?: boolean;
    className?: string;
    onSelect?: (enrollmentId: number) => void;
}

export const SelectorMembership: FunctionComponent<SelectorMembershipProps> = props => {
    const selectorRef = useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(props.selectedEnrollmentId);

    const [dropdownPosition, setDropdownPosition] = React.useState({
        top: 0,
        left: 0,
        width: 0
    });

    useEffect(() => {
        props.selectedEnrollmentId !== selectedId && setSelectedId(props.selectedEnrollmentId);
    }, [props.selectedEnrollmentId]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : 'auto';

        if (selectorRef.current) {
            const { top, left, width } = selectorRef.current.getBoundingClientRect();

            setDropdownPosition({
                top: top + selectorRef.current.scrollHeight,
                left: left,
                width: width
            });
        }
    }, [isOpen]);

    const selectedEnrollment = useMemo(
        () => props.enrollments.find(enrollment => enrollment.enrollmentId === selectedId),
        [props.enrollments, selectedId]
    );
    const label = useMemo(
        () =>
            selectedEnrollment ? (
                <span>{selectedEnrollment.membership.name}</span>
            ) : (
                <span className={styles.placeholder}>{props.placeholder ?? 'Select...'}</span>
            ),
        [selectedEnrollment, props.placeholder]
    );

    const selectHandler = (enrollmentId: number) => {
        if (!props.disabled) {
            setSelectedId(enrollmentId);
            props.onSelect && props.onSelect(enrollmentId);
            setIsOpen(false);
        }
    };

    return (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <div className={clsx(styles.container, props.className)}>
                <div
                    className={clsx(styles.header, {
                        [styles.collapsedHeader]: !isOpen,
                        [styles.expandedHeader]: isOpen
                    })}
                    onClick={() => setIsOpen(prev => !prev)}
                >
                    <div className={styles.left}>{label}</div>
                    <div className={styles.right}>
                        <FontAwesomeIcon
                            color="#7A7A7A"
                            fontSize={14}
                            icon={isOpen ? faChevronUp : faChevronDown}
                        />
                    </div>
                </div>
                {isOpen && (
                    <div className={styles.dropdown}>
                        <OverlayScrollbarsComponent style={{ maxHeight: 400 }}>
                            {props.enrollments.map(enrollment => (
                                <SelectorMembershipItem
                                    key={enrollment.enrollmentId}
                                    orderItemQuantity={props.orderItem.quantity}
                                    enrollment={enrollment}
                                    className={styles.dropdownLine}
                                    onClick={() => selectHandler(enrollment.enrollmentId)}
                                />
                            ))}
                        </OverlayScrollbarsComponent>
                    </div>
                )}
            </div>
        </ClickAwayListener>
    );
};

export default SelectorMembership;
